import { useQuery } from '@tanstack/react-query';

import { addonChartSource } from '@api/sdk.gen';
import { AddonsChartSourceStatusResponse } from '@api/types.gen';

import { RegistryId } from '@/react/portainer/registries/types/registry';
import { parseAxiosError } from '@/portainer/services/axios/axios';

import { queryKeys } from '../query-keys';

export interface AddonChartSourceStatus {
  /** Whether the registry answered. A chart it does not hold is still reachable. */
  reachable: boolean;
  /** Why the source could not be read. Empty when reachable. */
  reason: string;
  /** Whether the registry's certificate is verified when the chart is pulled. */
  tlsVerify: boolean;
  /** The chart's published versions this Portainer can run, latest-first. */
  versions: Array<string>;
  /** Whether the newest published version needs a newer Portainer. */
  portainerUpgradeRequired: boolean;
}

/**
 * useAddonChartSourceStatus checks where an add-on's chart is pulled from: the
 * catalog's own chart when no registry is given, or a chart in a Portainer
 * registry the user picked in the install form.
 *
 * The check is what tells an add-on whose chart simply is not there apart from
 * one whose registry cannot be read — an air-gapped mirror serving a certificate
 * Portainer does not trust, say. Both come back as an empty version list, and
 * only the second is fixed by pointing the install at a registry.
 *
 * A failed request resolves to an unreachable status rather than to an error:
 * the chart path is typed freely, so an unfinished one is an ordinary state of
 * the form, not a fault worth a toast.
 */
export function useAddonChartSourceStatus({
  addonId,
  registryId,
  chart,
  enabled = true,
}: {
  addonId: string;
  registryId?: RegistryId;
  chart: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: queryKeys.chartSource(addonId, registryId ?? 0, chart),
    queryFn: () => getChartSourceStatus(addonId, registryId, chart),
    // A registry is picked before the chart it holds, and there is nothing to
    // check until both are known. The catalog's own chart needs neither.
    enabled: enabled && !!addonId && (!registryId || !!chart),
    // A half-typed or mistyped path is an expected miss, not a transient fault.
    retry: false,
  });
}

async function getChartSourceStatus(
  addonId: string,
  registryId: RegistryId | undefined,
  chart: string
): Promise<AddonChartSourceStatus> {
  try {
    const { data } = await addonChartSource({
      path: { id: addonId },
      query: registryId ? { registryId, chart } : undefined,
    });

    return toStatus(data);
  } catch (err) {
    return {
      reachable: false,
      reason: parseAxiosError(
        err,
        'Portainer could not check where this chart is pulled from'
      ).message,
      tlsVerify: true,
      versions: [],
      portainerUpgradeRequired: false,
    };
  }
}

function toStatus(
  data: AddonsChartSourceStatusResponse | undefined
): AddonChartSourceStatus {
  return {
    reachable: data?.reachable ?? false,
    reason: data?.reason ?? '',
    // Absent only when the source could not be resolved at all, where reporting
    // verification as off would be a warning about nothing.
    tlsVerify: data?.tlsVerify ?? true,
    versions: data?.versions ?? [],
    portainerUpgradeRequired: data?.portainerUpgradeRequired ?? false,
  };
}
